/**
 * ui/services/drafts.ts
 *
 * Editor drafts on this device, and sharing one by an unlisted link.
 */
import type { DraftStorePort } from '../../ports/drafts.js';
import { IndexedDBDraftStore } from '../../adapters/indexed-db-drafts.js';
import { sharePuzzle } from '../../adapters/http-share.js';
import type { PuzzleDefinition } from '@nonogram/shared';
import {
  createDraft,
  draftFingerprint,
  draftFromCatalogPuzzle,
  draftToPuzzle,
  hasUnsavedCatalogChanges,
  type PuzzleDraft,
} from '../../editor/draft.js';
import { newId, ProfileService } from './profile.js';
import { recordDeletions } from './sync-deletions.js';

export class DraftService {
  static readonly instance = new DraftService(new IndexedDBDraftStore());

  private readonly _listeners = new Set<() => void>();

  constructor(
    private readonly _store: DraftStorePort,
    private readonly _share: typeof sharePuzzle = sharePuzzle,
    private readonly _now: () => number = Date.now,
    private readonly _newId: () => string = newId,
    private readonly _authorName: () => string = () => ProfileService.instance.get().name,
  ) {}

  list(): Promise<PuzzleDraft[]> {
    return this._store.listDrafts();
  }

  get(id: string): Promise<PuzzleDraft | null> {
    return this._store.getDraft(id);
  }

  async create(options: { name?: string; rows: number; columns: number }): Promise<PuzzleDraft> {
    const draft = createDraft({ ...options, id: this._newId(), now: this._now() });
    await this._store.saveDraft(draft);
    this._notify();
    return draft;
  }

  async save(draft: PuzzleDraft): Promise<PuzzleDraft> {
    const saved = { ...draft, updatedAt: this._now() };
    await this._store.saveDraft(saved);
    this._notify();
    return saved;
  }

  /**
   * The draft that edits a catalog puzzle (an admin's Edit in editor). A draft
   * for it with changes not yet saved to the catalog is kept, so they aren't
   * lost; otherwise the draft starts again from the puzzle as it is now.
   */
  async openCatalogPuzzle(puzzle: PuzzleDefinition): Promise<PuzzleDraft> {
    const linked = (await this._store.listDrafts()).filter((draft) => draft.catalog?.puzzleId === puzzle.id);
    const unsaved = linked.find(hasUnsavedCatalogChanges);
    if (unsaved) return unsaved;
    const [reused] = linked;
    const now = this._now();
    const draft = draftFromCatalogPuzzle(puzzle, { id: reused?.id ?? this._newId(), now });
    if (reused) draft.createdAt = reused.createdAt;
    await this._store.saveDraft(draft);
    this._notify();
    return draft;
  }

  async duplicate(draft: PuzzleDraft): Promise<PuzzleDraft> {
    const now = this._now();
    const copy: PuzzleDraft = { ...structuredClone(draft), id: this._newId(), name: `${draft.name} (copy)`, createdAt: now, updatedAt: now };
    delete copy.shared;
    // A copy is a new puzzle, not another way to change the catalog one.
    delete copy.catalog;
    await this._store.saveDraft(copy);
    this._notify();
    return copy;
  }

  async delete(id: string): Promise<void> {
    await this._store.deleteDraft(id);
    recordDeletions('draft', [id]);
    this._notify();
  }

  /** Called after any draft is created, saved, or deleted here. */
  onChanged(listener: () => void): () => void {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  private _notify(): void {
    for (const listener of this._listeners) listener();
  }

  /** True when the draft was shared and hasn't changed since. */
  isShared(draft: PuzzleDraft): boolean {
    return Boolean(draft.shared && draft.shared.fingerprint === draftFingerprint(draft));
  }

  /**
   * Shares the draft by an unlisted link. A shared puzzle never changes;
   * sharing after edits makes a new link. Returns the share record to keep
   * on the draft; the caller saves it (the draft may have changed meanwhile,
   * so saving the copy passed in here could undo those edits).
   */
  async share(draft: PuzzleDraft): Promise<NonNullable<PuzzleDraft['shared']>> {
    if (draft.shared && this.isShared(draft)) return draft.shared;
    const puzzle = draftToPuzzle(draft);
    const author = this._authorName().trim();
    const puzzleId = await this._share({ ...puzzle, ...(author ? { author } : {}) });
    return { puzzleId, fingerprint: draftFingerprint(draft), sharedAt: this._now() };
  }
}

/** The link friends open to play a shared puzzle. */
export function sharedPuzzleUrl(puzzleId: string, origin: string = globalThis.location?.origin ?? ''): string {
  return `${origin}/puzzle/${encodeURIComponent(puzzleId)}`;
}
